/**
 * PRJ-#### label for a case. Visit payloads may already carry the number; otherwise the case strip is the source.
 * A 404 or 501 means this surface has no project. Any other failure stays "unreachable" (hard rule 10).
 */
import { useQuery } from '@tanstack/react-query'
import { ApiError } from '../../lib/api/client'
import type { VisitOut } from '../../lib/api/generated'
import { caseProjectStripQuery } from './api'

export function projectReadAbsent(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 501)
}

export function projectNumberFromVisit(visit: VisitOut): string | null {
  const extra = visit as VisitOut & {
    project_number?: string | null
    project?: { number?: string | null } | null
  }
  const number = extra.project_number ?? extra.project?.number ?? null
  const trimmed = number?.trim()
  return trimmed ? trimmed : null
}

export type CaseProject =
  | { status: 'pending' }
  | { status: 'none' }
  | { status: 'unreachable' }
  | { status: 'project'; number: string; projectId: string | null }

export function useCaseProject(caseId: string | null, knownNumber?: string | null): CaseProject {
  const q = useQuery({ ...caseProjectStripQuery(caseId ?? 'none'), enabled: Boolean(caseId) && !knownNumber })
  if (knownNumber) return { status: 'project', number: knownNumber, projectId: null }
  if (!caseId) return { status: 'none' }
  if (q.isPending) return { status: 'pending' }
  if (q.isError) return projectReadAbsent(q.error) ? { status: 'none' } : { status: 'unreachable' }
  if (q.data.variant === 'none' || !q.data.number) return { status: 'none' }
  return { status: 'project', number: q.data.number, projectId: q.data.project_id ?? null }
}

export function VisitProjectMark({ visit }: { visit: VisitOut }) {
  const project = useCaseProject(visit.case_id, projectNumberFromVisit(visit))
  if (project.status === 'pending' || project.status === 'none') return null
  if (project.status === 'unreachable') {
    return (
      <p role="status" className="text-xs text-warning">
        Project label unreachable
      </p>
    )
  }
  return <p className="text-xs font-medium">{project.number}</p>
}

/** Time-prompt description. The sheet title stays "Log time"; the project is named only when the case has one. */
export function LogTimeProjectDescription({
  caseId,
  number,
  title,
  active,
}: {
  caseId: string
  number: string
  title: string
  active: boolean
}) {
  const project = useCaseProject(active ? caseId : null)
  if (project.status === 'project')
    return (
      <>
        Log time → {project.number} ({number})
      </>
    )
  if (project.status === 'unreachable') {
    return (
      <>
        {number} · {title}. Project label unreachable.
      </>
    )
  }
  return (
    <>
      {number} · {title}
    </>
  )
}
